import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Collapse,
  IconButton,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  MedicalServices as MedicalServicesIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import api from '../../api/axios';

const CLASSIFICATION_LABELS = {
  flow_config: { label: 'Fixable in flow config', color: 'success' },
  platform_gap: { label: 'Needs platform code change', color: 'error' },
  needs_human: { label: 'Needs clarification', color: 'warning' }
};

/**
 * Diagnose & Fix Panel
 * Lets the admin describe a client issue; the fixer agent gathers evidence,
 * classifies the failure, and either proposes a backtested patch (previewed on
 * the canvas for human approval) or creates an admin escalation.
 */
const DiagnosePanel = ({ sessionId, flowSteps, onPatchPreview }) => {
  const [open, setOpen] = useState(false);
  const [issue, setIssue] = useState('');
  const [executionId, setExecutionId] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const runDiagnosis = async () => {
    if (!issue.trim()) return;
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const response = await api.post('/ai-flow-builder/diagnose', {
        sessionId,
        issueDescription: issue,
        steps: flowSteps,
        executionId: executionId.trim() || null
      });
      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setRunning(false);
    }
  };

  const applyPatchPreview = () => {
    if (result?.patchFlow && onPatchPreview) {
      onPatchPreview(result.patchFlow);
    }
  };

  const classificationChip = () => {
    if (!result) return null;
    const info = CLASSIFICATION_LABELS[result.classification] || { label: result.classification, color: 'default' };
    return <Chip size="small" label={info.label} color={info.color} />;
  };

  return (
    <Box sx={{ borderTop: '1px solid #e0e0e0', bgcolor: '#fbf7f2' }}>
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, cursor: 'pointer' }}
        onClick={() => setOpen(!open)}
      >
        <MedicalServicesIcon fontSize="small" color="action" />
        <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
          Diagnose &amp; Fix a client issue
        </Typography>
        {classificationChip()}
        <IconButton size="small">
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={open}>
        <Box sx={{ px: 2, pb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <TextField
            label="What went wrong? Describe the issue"
            placeholder="e.g. The unit price column shows the total price instead of price per unit for client X's delivery orders"
            value={issue}
            onChange={(e) => setIssue(e.target.value)}
            multiline
            minRows={2}
            size="small"
            fullWidth
          />
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              label="Execution ID (optional)"
              value={executionId}
              onChange={(e) => setExecutionId(e.target.value)}
              size="small"
              sx={{ flexGrow: 1 }}
            />
            <Button
              variant="contained"
              onClick={runDiagnosis}
              disabled={running || !issue.trim()}
              startIcon={running ? <CircularProgress size={14} /> : <MedicalServicesIcon />}
            >
              {running ? 'Diagnosing…' : 'Diagnose'}
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Optional evidence: attach the failing sample file(s) with the paperclip in the chat
            input above, and/or paste an Execution ID from a failed run. Either helps the agent
            reproduce the issue. The agent never applies changes without your approval.
          </Typography>

          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {result && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {result.reasoning}
              </Typography>

              {result.suspectedStep && (
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  Suspected step: <b>{result.suspectedStep}</b>
                </Typography>
              )}

              {result.clarifyingQuestions?.length > 0 && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  <b>The agent needs more information:</b>
                  <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                    {result.clarifyingQuestions.map((q, i) => (
                      <li key={i}>{q}</li>
                    ))}
                  </ul>
                </Alert>
              )}

              {result.patch && (
                <Alert
                  severity={result.patchBacktest?.passed === true ? 'success' : 'info'}
                  sx={{ mt: 1 }}
                  action={
                    result.patchFlow && (
                      <Button size="small" variant="outlined" onClick={applyPatchPreview}>
                        Preview Fix
                      </Button>
                    )
                  }
                >
                  <b>Proposed fix:</b> {result.patch.explanation}
                  {result.patchBacktest && (
                    <div style={{ marginTop: 4 }}>
                      Backtest: {result.patchBacktest.passed === true ? '✅ passed' : result.patchBacktest.passed === false ? '❌ failed' : '⚠️ needs review'} — {result.patchBacktest.summary}
                    </div>
                  )}
                </Alert>
              )}

              {result.patchError && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Could not generate a valid config patch: {result.patchError}
                </Alert>
              )}

              {result.modelWarnings?.length > 0 && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  <b>Missing API key(s):</b>
                  <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                    {result.modelWarnings.map((w, i) => (
                      <li key={i}>{w.message}</li>
                    ))}
                  </ul>
                </Alert>
              )}

              {result.classification === 'platform_gap' && result.missingCapability && (
                <Alert severity={result.workaroundAvailable ? 'info' : 'error'} sx={{ mt: 1 }}>
                  <b>Missing capability:</b> {result.missingCapability}
                  {result.workaroundAvailable && result.workaroundDescription ? (
                    <div style={{ marginTop: 4 }}>✅ Workaround available now: {result.workaroundDescription}</div>
                  ) : result.suggestedFeature ? (
                    <div style={{ marginTop: 4 }}>🛠️ Suggested platform feature: {result.suggestedFeature}</div>
                  ) : null}
                </Alert>
              )}

              {result.escalationId && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  This needs a platform code change. An admin escalation with a Cursor-ready fix
                  prompt was created: <b>{result.escalationId}</b> (see the Escalations page).
                </Alert>
              )}
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default DiagnosePanel;
